import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import {
  Building2,
  Plus,
  FileText,
  Sparkles,
  Rocket,
  Shield,
  Clock,
  CheckCircle2,
  Users,
  TrendingUp,
  AlertCircle,
  Calendar,
  Activity,
  Zap,
  BookOpen,
  ScrollText,
  UserPlus,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  dashboardApi,
  type Company,
  type PendingAction,
  type UpcomingDeadline,
  type RecentActivity,
} from "@/lib/api";
import { CreateCompanyModal } from "@/components/CreateCompanyModal";
import { Spinner } from "@/components/ui/spinner";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";

interface DashboardStats {
  totalCompanies: number;
  activeReports: number;
  completedReports: number;
}

// Benefits for the hero section
const BENEFITS = [
  { icon: Clock, text: "Spara timmar varje år" },
  { icon: Shield, text: "100% regelrätt" },
  { icon: CheckCircle2, text: "Bolagsverket-godkänt" },
];

// Helper function to format relative time
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just nu";
  if (diffMins < 60) return `${diffMins} min sedan`;
  if (diffHours < 24) return `${diffHours} tim sedan`;
  if (diffDays === 1) return "Igår";
  if (diffDays < 7) return `${diffDays} dagar sedan`;
  return date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
}

// Helper function to format deadline
function formatDeadline(daysRemaining: number): string {
  if (daysRemaining === 0) return "Idag";
  if (daysRemaining === 1) return "Imorgon";
  if (daysRemaining <= 7) return `Om ${daysRemaining} dagar`;
  if (daysRemaining <= 30) return `Om ${Math.ceil(daysRemaining / 7)} veckor`;
  return `Om ${Math.ceil(daysRemaining / 30)} månader`;
}

export function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalCompanies: 0,
    activeReports: 0,
    completedReports: 0,
  });
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState<UpcomingDeadline[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOverviewLoading, setIsOverviewLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setIsOverviewLoading(true);

    try {
      // Load stats and overview in parallel
      const [statsResponse, overviewResponse] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getOverview(),
      ]);

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data.stats);
      }

      if (overviewResponse.success && overviewResponse.data) {
        setPendingActions(overviewResponse.data.pendingActions);
        setUpcomingDeadlines(overviewResponse.data.upcomingDeadlines);
        setRecentActivity(overviewResponse.data.recentActivity);
      }
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      toast.error("Kunde inte ladda dashboard-data");
    } finally {
      setIsLoading(false);
      setIsOverviewLoading(false);
    }
  };

  const handleCompanyCreated = (company: Company) => {
    setStats((prev) => ({
      ...prev,
      totalCompanies: prev.totalCompanies + 1,
    }));
    navigate(`/dashboard/companies/${company.id}`);
  };

  const handleActionClick = (action: PendingAction) => {
    if (action.reportId) {
      navigate(`/dashboard/wizard/${action.reportId}/step/1`);
    } else {
      navigate(`/dashboard/companies/${action.companyId}`);
    }
  };

  const handleDeadlineClick = (deadline: UpcomingDeadline) => {
    if (deadline.reportId) {
      navigate(`/dashboard/wizard/${deadline.reportId}/step/1`);
    } else {
      navigate(`/dashboard/companies/${deadline.companyId}`);
    }
  };

  const handleActivityClick = (activity: RecentActivity) => {
    if (activity.reportId) {
      // Navigate without step - the wizard will redirect to the appropriate step
      // based on the report's currentStep and validation rules
      navigate(`/dashboard/wizard/${activity.reportId}`);
    } else if (activity.companyId) {
      navigate(`/dashboard/companies/${activity.companyId}`);
    }
  };

  const firstName = user?.firstName || "Användare";

  // Activity type icons
  const activityIcons: Record<string, typeof FileText> = {
    report_exported: FileText,
    report_updated: FileText,
    report_created: FileText,
    company_added: Building2,
    sharebook_transaction: BookOpen,
    shareholder_added: UserPlus,
    protocol_created: ScrollText,
    protocol_finalized: ScrollText,
  };

  return (
    <>
      <PageMeta
        title={DASHBOARD_PAGE_META.dashboard.title}
        description={DASHBOARD_PAGE_META.dashboard.description}
        noIndex
      />
      <div className="space-y-8">
        {/* ═══════════════════════════════════════════════════════════════════
            HERO SECTION - Welcome & Quick Stats
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 shadow-2xl">
          {/* Decorative elements */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            {/* Welcome text */}
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                <Sparkles className="text-amber-400" size={14} />
                <span className="text-white/80 text-xs font-medium tracking-wide">Din företagsportal</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight">
                Välkommen, {firstName}
              </h1>
              <p className="text-slate-300 max-w-lg leading-relaxed">
                Allt du behöver för att hantera ditt företags administration –
                årsredovisningar, aktiebok, protokoll och mer.
              </p>

              {/* Benefits */}
              <div className="flex flex-wrap gap-5 mt-6">
                {BENEFITS.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 text-slate-300 text-sm">
                    <div className="w-7 h-7 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
                      <benefit.icon size={14} className="text-white" />
                    </div>
                    <span>{benefit.text}</span>
                  </div>
                ))}
              </div>

              {/* Hero CTAs */}
              <div className="flex flex-wrap gap-3 mt-8">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 px-5 py-3 shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                    <div className="relative flex items-center gap-2.5 text-white font-medium">
                      <Plus size={18} />
                      <span>Lägg till företag</span>
                    </div>
                  </button>
                </div>
                <Link
                  to="/dashboard/companies"
                  className="group relative overflow-hidden rounded-xl bg-white/10 backdrop-blur-sm px-5 py-3 border border-white/10 hover:bg-white/15 hover:border-white/20 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  <div className="relative flex items-center gap-2.5 text-white font-medium">
                    <Building2 size={18} />
                    <span>Mina företag</span>
                    <ChevronRight size={16} className="text-white/60 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              </div>
            </div>

            {/* Quick stats */}
            <div className="flex flex-row gap-3 sm:gap-4">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-primary-600/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all opacity-75" />
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl px-6 py-5 min-w-[120px] border border-white/10 hover:border-white/20 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 size={16} className="text-primary-300" />
                    <span className="text-white/60 text-xs uppercase tracking-wider font-medium">Företag</span>
                  </div>
                  {isLoading ? (
                    <Spinner size="sm" className="text-white" />
                  ) : (
                    <p className="text-4xl font-bold text-white tracking-tight">{stats.totalCompanies}</p>
                  )}
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all opacity-75" />
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl px-6 py-5 min-w-[120px] border border-white/10 hover:border-white/20 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={16} className="text-emerald-300" />
                    <span className="text-white/60 text-xs uppercase tracking-wider font-medium">Pågående</span>
                  </div>
                  {isLoading ? (
                    <Spinner size="sm" className="text-white" />
                  ) : (
                    <p className="text-4xl font-bold text-white tracking-tight">{stats.activeReports}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            DYNAMIC CONTENT SECTION
            Layout logic:
            - Both actions & deadlines → 2-col row, then activity full width
            - Only actions → actions | activity side by side
            - Only deadlines → deadlines | activity side by side
            - Neither → activity full width
        ═══════════════════════════════════════════════════════════════════ */}
        {stats.totalCompanies > 0 && !isOverviewLoading && (
          <>
            {/* Case 1: Both actions AND deadlines exist - show in their own row */}
            {pendingActions.length > 0 && upcomingDeadlines.length > 0 && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Pending Actions */}
                  <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                        <AlertCircle size={20} className="text-white" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-gray-900">Kräver åtgärd</h2>
                        <p className="text-xs text-gray-500">Saker som behöver din uppmärksamhet</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {pendingActions.map((action) => (
                        <button
                          key={action.id}
                          onClick={() => handleActionClick(action)}
                          className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                        >
                          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100 group-hover:shadow transition-shadow">
                            <Zap size={16} className="text-amber-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-medium text-gray-900 text-sm truncate">{action.title}</span>
                              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                action.priority === "high" ? "bg-red-100 text-red-700" :
                                action.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"
                              }`}>
                                {action.priority === "high" ? "Hög" : action.priority === "medium" ? "Mellan" : "Låg"}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">{action.description}</p>
                          </div>
                          <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 flex-shrink-0 mt-1.5 transition-all" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Upcoming Deadlines */}
                  <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                        <Calendar size={20} className="text-white" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-gray-900">Kommande deadlines</h2>
                        <p className="text-xs text-gray-500">Datum att hålla koll på</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {upcomingDeadlines.map((deadline) => (
                        <button
                          key={deadline.id}
                          onClick={() => handleDeadlineClick(deadline)}
                          className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border ${
                            deadline.priority === "high" ? "bg-red-50 border-red-100" :
                            deadline.priority === "medium" ? "bg-amber-50 border-amber-100" : "bg-white border-gray-100"
                          }`}>
                            <Clock size={16} className={
                              deadline.priority === "high" ? "text-red-500" : deadline.priority === "medium" ? "text-amber-500" : "text-gray-500"
                            } />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-medium text-gray-900 text-sm truncate">{deadline.title}</span>
                              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                deadline.priority === "high" ? "bg-red-100 text-red-700" :
                                deadline.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"
                              }`}>
                                {formatDeadline(deadline.daysRemaining)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">{deadline.description}</p>
                          </div>
                          <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 flex-shrink-0 mt-1.5 transition-all" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Activity - full width below */}
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                      <Activity size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">Senaste aktivitet</h2>
                      <p className="text-xs text-gray-500">Dina senaste händelser</p>
                    </div>
                  </div>
                  {recentActivity.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                        <Activity size={24} className="text-gray-400" />
                      </div>
                      <p className="text-gray-600 font-medium">Ingen aktivitet ännu</p>
                      <p className="text-gray-400 text-sm mt-1">Din aktivitet kommer visas här</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {recentActivity.map((activity) => {
                        const Icon = activityIcons[activity.type] || Activity;
                        return (
                          <button
                            key={activity.id}
                            onClick={() => handleActivityClick(activity)}
                            className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                          >
                            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100 group-hover:shadow transition-shadow">
                              <Icon size={16} className="text-violet-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{activity.title}</p>
                              <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                              <p className="text-[10px] text-gray-400 mt-1 font-medium">{formatRelativeTime(activity.timestamp)}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Case 2: Only actions exist - show actions + activity side by side */}
            {pendingActions.length > 0 && upcomingDeadlines.length === 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Pending Actions */}
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                      <AlertCircle size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">Kräver åtgärd</h2>
                      <p className="text-xs text-gray-500">Saker som behöver din uppmärksamhet</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {pendingActions.map((action) => (
                      <button
                        key={action.id}
                        onClick={() => handleActionClick(action)}
                        className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                      >
                        <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100 group-hover:shadow transition-shadow">
                          <Zap size={16} className="text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-medium text-gray-900 text-sm truncate">{action.title}</span>
                            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                              action.priority === "high" ? "bg-red-100 text-red-700" :
                              action.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"
                            }`}>
                              {action.priority === "high" ? "Hög" : action.priority === "medium" ? "Mellan" : "Låg"}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{action.description}</p>
                        </div>
                        <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 flex-shrink-0 mt-1.5 transition-all" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Activity - side by side */}
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                      <Activity size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">Senaste aktivitet</h2>
                      <p className="text-xs text-gray-500">Dina senaste händelser</p>
                    </div>
                  </div>
                  {recentActivity.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                        <Activity size={24} className="text-gray-400" />
                      </div>
                      <p className="text-gray-600 font-medium">Ingen aktivitet ännu</p>
                      <p className="text-gray-400 text-sm mt-1">Din aktivitet kommer visas här</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {recentActivity.slice(0, 5).map((activity) => {
                        const Icon = activityIcons[activity.type] || Activity;
                        return (
                          <button
                            key={activity.id}
                            onClick={() => handleActivityClick(activity)}
                            className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                          >
                            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100 group-hover:shadow transition-shadow">
                              <Icon size={16} className="text-violet-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{activity.title}</p>
                              <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                              <p className="text-[10px] text-gray-400 mt-1 font-medium">{formatRelativeTime(activity.timestamp)}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Case 3: Only deadlines exist - show deadlines + activity side by side */}
            {pendingActions.length === 0 && upcomingDeadlines.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Upcoming Deadlines */}
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <Calendar size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">Kommande deadlines</h2>
                      <p className="text-xs text-gray-500">Datum att hålla koll på</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {upcomingDeadlines.map((deadline) => (
                      <button
                        key={deadline.id}
                        onClick={() => handleDeadlineClick(deadline)}
                        className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border ${
                          deadline.priority === "high" ? "bg-red-50 border-red-100" :
                          deadline.priority === "medium" ? "bg-amber-50 border-amber-100" : "bg-white border-gray-100"
                        }`}>
                          <Clock size={16} className={
                            deadline.priority === "high" ? "text-red-500" : deadline.priority === "medium" ? "text-amber-500" : "text-gray-500"
                          } />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-medium text-gray-900 text-sm truncate">{deadline.title}</span>
                            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                              deadline.priority === "high" ? "bg-red-100 text-red-700" :
                              deadline.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"
                            }`}>
                              {formatDeadline(deadline.daysRemaining)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{deadline.description}</p>
                        </div>
                        <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 flex-shrink-0 mt-1.5 transition-all" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Activity - side by side */}
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                      <Activity size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">Senaste aktivitet</h2>
                      <p className="text-xs text-gray-500">Dina senaste händelser</p>
                    </div>
                  </div>
                  {recentActivity.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                        <Activity size={24} className="text-gray-400" />
                      </div>
                      <p className="text-gray-600 font-medium">Ingen aktivitet ännu</p>
                      <p className="text-gray-400 text-sm mt-1">Din aktivitet kommer visas här</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {recentActivity.slice(0, 5).map((activity) => {
                        const Icon = activityIcons[activity.type] || Activity;
                        return (
                          <button
                            key={activity.id}
                            onClick={() => handleActivityClick(activity)}
                            className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                          >
                            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100 group-hover:shadow transition-shadow">
                              <Icon size={16} className="text-violet-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{activity.title}</p>
                              <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                              <p className="text-[10px] text-gray-400 mt-1 font-medium">{formatRelativeTime(activity.timestamp)}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Case 4: Neither actions nor deadlines - activity full width */}
            {pendingActions.length === 0 && upcomingDeadlines.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                    <Activity size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Senaste aktivitet</h2>
                    <p className="text-xs text-gray-500">Dina senaste händelser</p>
                  </div>
                </div>
                {recentActivity.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                      <Activity size={24} className="text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium">Ingen aktivitet ännu</p>
                    <p className="text-gray-400 text-sm mt-1">Din aktivitet kommer visas här</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {recentActivity.map((activity) => {
                      const Icon = activityIcons[activity.type] || Activity;
                      return (
                        <button
                          key={activity.id}
                          onClick={() => handleActivityClick(activity)}
                          className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/50 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50 transition-all text-left group"
                        >
                          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100 group-hover:shadow transition-shadow">
                            <Icon size={16} className="text-violet-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{activity.title}</p>
                            <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                            <p className="text-[10px] text-gray-400 mt-1 font-medium">{formatRelativeTime(activity.timestamp)}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            GETTING STARTED - Onboarding guide (only for new users)
        ═══════════════════════════════════════════════════════════════════ */}
        {stats.totalCompanies === 0 && (
          <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-200/80 shadow-sm p-8">
            {/* Decorative gradient */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary-50 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />

            <div className="relative flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center shadow-lg">
                <Rocket className="text-white" size={22} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Kom igång</h2>
                <p className="text-gray-500 text-sm">Tre enkla steg för att börja använda Kvanten</p>
              </div>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="relative">
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-lg shadow-primary-500/25">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Lägg till ditt företag</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Ange organisationsnummer så hämtar vi grundläggande uppgifter automatiskt från Bolagsverket.
                    </p>
                  </div>
                </div>
                {/* Connector line - hidden on mobile */}
                <div className="hidden md:block absolute top-5 left-[calc(100%+8px)] w-[calc(100%-20px)] h-px bg-gradient-to-r from-gray-200 to-transparent" />
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-lg shadow-emerald-500/25">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Aktivera företaget</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Vid aktivering får du tillgång till alla funktioner – aktiebok, årsredovisningar, protokoll och mer.
                    </p>
                  </div>
                </div>
                {/* Connector line - hidden on mobile */}
                <div className="hidden md:block absolute top-5 left-[calc(100%+8px)] w-[calc(100%-20px)] h-px bg-gradient-to-r from-gray-200 to-transparent" />
              </div>

              {/* Step 3 */}
              <div>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-lg shadow-violet-500/25">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Börja arbeta</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Hantera aktieägare, skapa protokoll, generera årsredovisningar – allt regelrätt och på ett ställe.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="relative mt-10 pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-gray-500">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Users size={16} className="text-gray-500" />
                </div>
                <span className="text-sm font-medium">
                  Du har inga företag ännu – kom igång nu!
                </span>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="group inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 hover:-translate-y-0.5 transition-all duration-300"
              >
                <Plus size={18} />
                Lägg till ditt första företag
              </button>
            </div>
          </div>
        )}

        {/* Create Company Modal */}
        <CreateCompanyModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCompanyCreated={handleCompanyCreated}
        />
      </div>
    </>
  );
}
